import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiCommunity from "./useApiCommunity";

const invalidateCommunityQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({ queryKey: ["communities"] });
};

export const useCommunity = (id?: number) => {
  const { getCommunity } = useApiCommunity();

  return useQuery({
    queryKey: ["communities", "detail", id],
    queryFn: () => getCommunity(id as number),
    enabled: id !== undefined && !Number.isNaN(id),
    staleTime: 1000 * 60 * 5,
  });
};

export const useMyCommunity = (token: string) => {
  const { getMyCommunity } = useApiCommunity();

  return useQuery({
    queryKey: ["communities", "mine", token],
    queryFn: () => getMyCommunity(token),
    enabled: Boolean(token),
    staleTime: 1000 * 60 * 2,
  });
};

export const useRequestToJoinCommunity = () => {
  const queryClient = useQueryClient();
  const { requestToJoin } = useApiCommunity();

  return useMutation({
    mutationFn: ({
      communityId,
      token,
    }: {
      communityId: number;
      token: string;
    }) => requestToJoin(communityId, token),
    onSuccess: () => invalidateCommunityQueries(queryClient),
  });
};

export const useLeaveCommunity = () => {
  const queryClient = useQueryClient();
  const { leaveCommunity } = useApiCommunity();

  return useMutation({
    mutationFn: (token: string) => leaveCommunity(token),
    onSuccess: () => invalidateCommunityQueries(queryClient),
  });
};

export const useAcceptCommunityRequest = () => {
  const queryClient = useQueryClient();
  const { acceptRequest } = useApiCommunity();

  return useMutation({
    mutationFn: ({
      communityId,
      requesterId,
      token,
    }: {
      communityId: number;
      requesterId: number;
      token: string;
    }) => acceptRequest(communityId, requesterId, token),
    onSuccess: () => invalidateCommunityQueries(queryClient),
  });
};

export const useRejectCommunityRequest = () => {
  const queryClient = useQueryClient();
  const { rejectRequest } = useApiCommunity();

  return useMutation({
    mutationFn: ({
      communityId,
      requesterId,
      token,
    }: {
      communityId: number;
      requesterId: number;
      token: string;
    }) => rejectRequest(communityId, requesterId, token),
    onSuccess: () => invalidateCommunityQueries(queryClient),
  });
};
